import { useState, type FormEvent } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const FORM_URL = "https://readdy.ai/api/form/db6g28nqhq9pk5mectlg";

type FormStatus = "idle" | "submitting" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const FEATURES = [
  {
    icon: "ri-movie-2-line",
    title: "丰富影片库",
    text: "覆盖都市、古装、悬疑、江湖与奇幻等多元题材，持续更新最新三级影片资源。",
  },
  {
    icon: "ri-search-line",
    title: "便捷查找",
    text: "支持按片名与剧情关键词搜索，并可按地区、年代与热度多维筛选排序。",
  },
  {
    icon: "ri-heart-3-line",
    title: "本地收藏",
    text: "收藏喜欢的影片，记录保存在浏览器本地，无需注册登录即可使用。",
  },
];

export default function About() {
  useDocumentTitle(
    "关于我们 - 三级片电影",
    "了解三级片电影的站点定位、内容范围、使用说明与免责声明，并可提交内容反馈。"
  );

  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      form.reset();
      setStatus("success");
      return;
    }
    formData.delete("contact_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") {
        body.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK") {
        form.reset();
        setStatus("success");
        return;
      }

      setFormError(isSpam ? "提交未通过安全校验，请稍后再试。" : serverMsg || "提交失败，请稍后再试。");
      setStatus("error");
    } catch {
      setFormError("网络异常，提交失败，请稍后再试。");
      setStatus("error");
    }
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 pt-6 md:pt-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        <header className="mt-6 max-w-3xl">
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">关于我们</h1>
          <p className="text-sm md:text-base leading-relaxed text-foreground-600">
            三级片电影是一个专注于三级影片内容展示与信息聚合的影视门户。我们围绕都市、古装、悬疑、江湖与奇幻等
            多元题材，为用户提供最新的影片资讯、剧情介绍与分类浏览服务，帮助影迷更便捷地发现与了解感兴趣的作品。
          </p>
          <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
            本站面向成年影视爱好者，定位为内容展示与信息参考平台。站内影片资料均为演示内容，不提供任何形式的在线
            播放、下载或资源分发服务。
          </p>
        </header>

        <section className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {FEATURES.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200/70 bg-background-100 p-5">
              <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500/15 text-primary-300 mb-4">
                <i className={`${item.icon} text-2xl`} />
              </span>
              <h2 className="font-heading text-base font-semibold text-foreground-950 mb-2">{item.title}</h2>
              <p className="text-sm leading-relaxed text-foreground-500">{item.text}</p>
            </div>
          ))}
        </section>

        <section className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          <div className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">使用说明</h2>
            <ul className="space-y-3 text-sm leading-relaxed text-foreground-600">
              <li className="flex gap-3">
                <i className="ri-arrow-right-s-line text-accent-500 text-lg shrink-0" />
                通过首页分类入口或顶部导航，浏览不同题材的影片列表。
              </li>
              <li className="flex gap-3">
                <i className="ri-arrow-right-s-line text-accent-500 text-lg shrink-0" />
                使用搜索框输入片名或剧情关键词，快速定位想看的内容。
              </li>
              <li className="flex gap-3">
                <i className="ri-arrow-right-s-line text-accent-500 text-lg shrink-0" />
                在影片详情页可查看完整资料、剧情介绍与相关推荐。
              </li>
              <li className="flex gap-3">
                <i className="ri-arrow-right-s-line text-accent-500 text-lg shrink-0" />
                点击收藏按钮即可把影片加入收藏，收藏记录保存在本地浏览器。
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">免责声明</h2>
            <ul className="space-y-3 text-sm leading-relaxed text-foreground-600">
              <li className="flex gap-3">
                <i className="ri-error-warning-line text-primary-300 text-lg shrink-0" />
                本站所有影片信息与剧情文本均为演示用示例内容，不代表真实作品。
              </li>
              <li className="flex gap-3">
                <i className="ri-error-warning-line text-primary-300 text-lg shrink-0" />
                本站不存放、不传播任何影视资源，不提供在线播放与下载服务。
              </li>
              <li className="flex gap-3">
                <i className="ri-error-warning-line text-primary-300 text-lg shrink-0" />
                本站内容仅面向成年人，请在符合当地法律法规的前提下浏览。
              </li>
              <li className="flex gap-3">
                <i className="ri-error-warning-line text-primary-300 text-lg shrink-0" />
                收藏与搜索功能均为前端演示，数据仅保存在你的浏览器本地。
              </li>
            </ul>
          </div>
        </section>

        <section className="mt-12 mb-4">
          <div className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8 max-w-3xl">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-2">联系我们</h2>
            <p className="text-sm text-foreground-500 mb-6">
              有内容建议、影片收录或合作意向？填写下面的表单，我们会尽快与你联系。
            </p>

            {status === "success" ? (
              <div className="flex items-center gap-3 rounded-md border border-accent-500/40 bg-accent-500/10 px-4 py-4 text-sm text-foreground-800">
                <i className="ri-checkbox-circle-fill text-accent-500 text-xl" />
                反馈已提交，感谢你的留言！
              </div>
            ) : (
              <form
                id="contact-feedback-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="contact-name" className="text-sm text-foreground-600">
                      昵称
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      required
                      placeholder="请输入你的称呼"
                      className="rounded-md border border-background-200/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="contact-email" className="text-sm text-foreground-600">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="name@example.com"
                      className="rounded-md border border-background-200/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-topic" className="text-sm text-foreground-600">
                    反馈类型
                  </label>
                  <select
                    id="contact-topic"
                    name="topic"
                    required
                    defaultValue=""
                    className="rounded-md border border-background-200/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                  >
                    <option value="" disabled>
                      请选择反馈类型
                    </option>
                    <option value="内容建议">内容建议</option>
                    <option value="影片收录">影片收录</option>
                    <option value="问题反馈">问题反馈</option>
                    <option value="合作洽谈">合作洽谈</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-message" className="text-sm text-foreground-600">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请留下你的建议或需求（500 字以内）"
                    className="rounded-md border border-background-200/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors resize-none"
                  />
                </div>

                <input
                  type="text"
                  name="contact_alt"
                  className="contact-verify-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && formError && (
                  <div className="flex items-center gap-2 rounded-md border border-primary-500/40 bg-primary-500/10 px-4 py-3 text-sm text-foreground-800">
                    <i className="ri-error-warning-line text-primary-300" />
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-foreground-950 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap sm:self-start"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      提交反馈
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </section>
      </div>
      <div className="h-12 md:h-16" />
    </SiteLayout>
  );
}